import { ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, AfterViewInit, OnDestroy, ViewChild, Inject, PLATFORM_ID, inject, afterNextRender } from '@angular/core';
import { FrescorDoHtmlService } from 'src/app/core/services/ssr/frescor-do-html.service';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ModalController } from 'src/app/core/overlay';
import { computeCategoryNavScrollLeft } from './category-nav-scroll.util';
import { createScrollContentAdapter, ScrollContentAdapter } from 'src/app/shared/utils/scroll-content-adapter';
import { ActivatedRoute, NavigationStart, ResolveEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ScrollAnchor, ScrollSaveService } from 'src/app/core/services/scroll-save.service';
import { MenuDTO } from 'src/app/shared/model/dto/MenuDTO';
import { Menu, MenuItem } from 'src/app/shared/model/Menu';
import { SearchModalComponent } from './components/search-modal/search-modal.component';
import { RestaurantService } from 'src/app/core/services/restaurant.service';
import { Meta, Title } from '@angular/platform-browser';
import { Events } from 'src/app/core/services/events.service';
import { OnesignalService } from 'src/app/core/services/onesignal.service';
import { LocalStorageService } from "src/app/core/services/localStorage.service";
import { KeenTrackingService } from 'src/app/core/services/keenTracking.service';
import { OrderService } from 'src/app/core/services/order.service';
import { RestaurantLocalStorageService } from 'src/app/core/services/restaurantLocalStorage.service';
import { recommendProductService } from 'src/app/core/services/recommendProducts.service';
import { TableService } from 'src/app/core/services/table.service';

import type { LastOrderItem } from 'src/app/core/services/menu-validators.service';
import { ShoppingCartService } from 'src/app/core/services/shoppingCart.service';
import { normalizeOrderForCart } from 'src/app/core/services/cart-normalizer.service';
import { NavService } from 'src/app/core/services/nav.service';
import { environment } from 'src/environments/environment';
import { WabotClientLastOrderService } from 'src/app/core/services/wabot-client-last-order.service';
import { RemovedItemsDialogPage } from './components/removed-items-dialog/removed-items-dialog.page';
import { MenuListPage, PumpStats } from './components/menu-list/menu-list.page';
import { CategoryNavComponent } from './components/category-nav/category-nav.component';
import { OrderFlowTraceService } from 'src/app/core/services/telemetry/order-flow-trace.service';
import { MpBannerTraceService } from 'src/app/core/services/telemetry/mp-banner-trace.service';
import {
	buildFooterBannerLink,
	FOOTER_BANNER_TEXT,
	FooterBannerSlot,
	FooterBannerVariant,
	pickFooterBannerVariant,
} from './footer-banner-link';
import { agendarRelatorioFinal } from 'src/app/core/services/telemetry/lcp-observer';
import { caminhoDoDestaque, caminhoDoLogo, urlDaVersao, urlOriginal } from 'src/app/shared/utils/versao-de-imagem';

declare let fbq: any

/**
 * Uma volta de item em andamento: a página nova monta até a categoria da âncora, pousa, e
 * ainda pousa de novo quando o conteúdo ACIMA da lista (recomendações) assentar.
 */
interface ScrollRestore {
	anchor: ScrollAnchor;
	/** `scrollIntent` no início. Outro scroll programático (pill, "voltar ao topo") o avança. */
	intent: number;
	/** Onde a página estava na última vez que esta restauração olhou (ver `movedByScroll`). */
	seen: ScrollSnapshot;
	landed: boolean;
	/** Libera a suspensão do pump que a restauração segura até pousar. */
	releasePump: (() => void) | null;
	/** `performance.now()` do início — o tempo que a pessoa espera até pousar. */
	since: number;
	/** Por que o pouso não foi o ideal, para o `ClientMenuRestoreSettled`. */
	motivo: string;
	/** Um desfecho por restauração: o 2º pouso relata de novo com `repouso: true`. */
	settled: boolean;
}

/**
 * Uma posição vista da tela: o `scrollTop` e onde um nó de REFERÊNCIA estava na viewport.
 * Rolar muda os dois. O browser ajustando sozinho muda um só: o scroll anchoring do Chrome
 * compensa conteúdo que entra acima mudando o `scrollTop` e mantendo o nó no lugar; no
 * WebKit (sem anchoring) o nó desce e o `scrollTop` fica. Comparar só o `scrollTop` lia o
 * anchoring como "alguém rolou" e abortava o 2º pouso (rodada 2).
 */
interface ScrollSnapshot {
	top: number;
	reference: HTMLElement | null;
	referenceTop: number;
}

/** A pill que está esperando o pump montar a categoria dela. */
interface PendingPill {
	elementId: string;
	intent: number;
	/** De onde a pessoa pediu. */
	seen: ScrollSnapshot;
	/** Houve scroll que não é animação nossa desde o pedido (sem ler layout: roda no listener). */
	sawScroll: boolean;
	/** `performance.now()` do pedido: uma animação nossa rodando depois dele também mexe no scrollTop. */
	since: number;
}

/** Um dedo (ou botão do mouse) que desceu na página, enquanto o click dele ainda pode chegar. */
interface Press {
	/** O ALVO CLICÁVEL debaixo do dedo NA DESCIDA (`clickTargetOf`); `null`: nada acionável. */
	target: Element | null;
	/** `performance.now()` da descida. */
	at: number;
	/** `performance.now()` da subida; `null` com o dedo ainda na tela. */
	releasedAt: number | null;
}

/**
 * O que um click aciona dentro da página do cardápio, fora os cards: os `(click)` dos templates
 * (pills e segmentos do category-nav, select, busca, recomendações, cabeçalho, rodapé) e os
 * controles nativos.
 */
const CLICK_TARGETS = 'button, a, select, input, textarea, label, [role="button"], .mp-fab, .balance-cashback-fab, .mp-card, .mp-col, mp-icon, .menu-search-item, .recommended__card';

/**
 * O ALVO CLICÁVEL de um nó: o que um click nele aciona. Dentro de um card é o card inteiro —
 * nome, descrição, preço e foto são o mesmo produto (a foto abre o visualizador dele), e é o
 * card que um toque atravessado troca. Fora de card, o controle acionável mais próximo.
 */
function clickTargetOf(node: EventTarget | null): Element | null {
	const element = node instanceof Element ? node : node instanceof Node ? node.parentElement : null;
	if (!element) return null;
	return element.closest('card') ?? element.closest(CLICK_TARGETS);
}

@Component({
	selector: 'app-menu',
	templateUrl: './menu.page.html',
	styleUrls: ['./menu.page.scss'],
})
export class MenuPage implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('scrollContainer') scrollContainerRef: ElementRef<HTMLElement>;

	// Adaptador nativo com a API do IonContent (getScrollElement/scrollToTop/scrollToPoint/
	// scrollToBottom) sobre o <div> de scroll — sem o Web Component Ionic. Agora no util
	// compartilhado createScrollContentAdapter (mesma animação rAF/easeInOutQuad).
	get content(): ScrollContentAdapter {
		return createScrollContentAdapter(() => this.scrollContainerRef?.nativeElement);
	}
	@ViewChild('stickyBar') stickyBarRef: ElementRef<HTMLElement>;
	@ViewChild('footerBanner') footerBannerRef: ElementRef<HTMLElement>;
	@ViewChild(MenuListPage) menuList: MenuListPage;
	@ViewChild(CategoryNavComponent) categoryNav: CategoryNavComponent;

	restaurant: MenuDTO
	restaurantUrl: String
	loaded: boolean = this.restaurantService.loaded
	restaurantIsBlocked: boolean = false;

	// TODO: remove this when new pedir delivery is on 100% of the clients
	searchResults: Menu;
	private searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;

	categories = [];
	itemsToHide = { combos: true, pizzas: true, featuredItems: true };
	invalidURL = false
	/** O cardápio não chegou dentro do prazo do boot — dá para tentar de novo. */
	cargaExpirou = false
	recarregar() { window.location.reload() }
	availableProducts: any[] = [];
	recommendedProducts = [];
	showInstagramIosBanner: boolean = false;
	highlightImages: string[];
	imgURL: string = environment.imgURL;
	currentCategoryValue: string = '';
	isSticky = false;
	private currentScrollTop = 0;
	private scrollEl: HTMLElement | null = null;

	// ── Posição do cardápio: volta de item e pills do nav ────────────────────────────
	// Ver `restoreScroll` e `scrollToElement`. Nenhum dos dois chama `renderAllNow()`:
	// uma posição precisa do conteúdo ATÉ ela, e o pump garante que o que entra depois
	// entra ABAIXO (só a última categoria iniciada pode estar parcial).
	private static readonly SCROLL_KEY = 'menu';
	/** Prefixo dos avisos de degradação (pouso pelo pixel, alvo inalcançável etc.). */
	static readonly SCROLL_LOG = '[menu-scroll]';

	/** Desfecho da pill em curso, lido no `finally` do `scrollToCategory`. */
	private jumpOutcome = '';
	/** Duração da animação do `scrollToPoint` das pills. */
	static readonly CATEGORY_SCROLL_MS = 1000;
	/** Folga depois do fim de uma animação de scroll nossa: o último frame e o evento de scroll dele. */
	static readonly ANIMATION_TAIL_MS = 100;
	/**
	 * Quanto o fim REAL de uma animação nossa pode passar da duração pedida (ver
	 * `holdPumpDuringAnimation`). Passado isto sem o fim chegar — aba em segundo plano, rAF
	 * parado —, a janela fecha mesmo assim: nenhum marcador fica aberto para sempre.
	 */
	static readonly ANIMATION_MAX_LAG_MS = 2000;
	/**
	 * Até quanto depois da subida do dedo o click dela ainda chega. O click do toque é despachado
	 * depois do touchend/pointerup, e com a main thread ocupada (uma task do pump, o 1º frame da
	 * animação) a distância cresce. Passado isto, a subida não tem click a caminho.
	 */
	static readonly CLICK_AFTER_RELEASE_MS = 1000;
	/** Folga, em px, entre o topo da viewport e o topo da categoria escolhida na pill. */
	private static readonly CATEGORY_TOP_GAP = 50;
	/** Altura de card que o crescimento SUPÕE para pedir produtos ao pump. Pequena de
	 *  propósito: pedir a mais só adianta o que o pump montaria em seguida; pedir a menos
	 *  custa mais uma task até o alvo caber. */
	private static readonly GROW_PX_PER_PRODUCT = 60;

	/** Avança a cada scroll programático novo (pill, "voltar ao topo", volta de item); quem
	 *  estava esperando o pump para rolar compara com o seu e desiste se mudou. */
	private scrollIntent = 0;
	private restore: ScrollRestore | null = null;
	private pill: PendingPill | null = null;
	/** Animações de scroll nossas sem o fim REAL confirmado, cada uma com o prazo até o qual ainda pode andar. */
	private animationsInFlight = new Set<{ until: number }>();
	/** Até quando as animações de scroll nossas já terminadas andaram, com a folga (ver `animationWindowEnd`). */
	private animationsSettledUntil = 0;
	/** O toque ou clique VIVO na página: desceu e o click dele ainda pode chegar (ver `isClickThrough`). */
	private press: Press | null = null;
	/** O conteúdo acima da lista (recomendações) já assentou nesta montagem. */
	private contentAboveSettled = false;
	private destroyed = false;
	private scrollSettleTimer: ReturnType<typeof setTimeout> | null = null;
	/** Suspensões do pump seguradas durante animações de scroll (uma por animação). */
	private animationHolds = new Set<{ timer: ReturnType<typeof setTimeout>; release: () => void }>();
	private navigationSub: Subscription | null = null;
	private detachScrollListeners: (() => void) | null = null;

	get isNewHomePedirDelivery(): boolean {
		return !!(this.restaurant?.info?.modules?.new_home_pedir_delivery);
	}

	footerLinkLogo: string = '';
	footerLinkCta: string = '';
	footerLinkText: string = '';
	private footerVariant: FooterBannerVariant = 'nv-preco';

	/**
	 * Estado da medição do banner do rodapé. Tudo privado e sem binding: nada
	 * aqui entra no template, então não custa change detection.
	 */
	private bannerObserver: IntersectionObserver | null = null;
	private bannerViewedAt = 0;      // performance.now() da 1a vez que entrou na viewport (0 = nunca)
	private bannerViewEmitted = false;
	private pageEnteredAt = 0;       // relógio da permanência na página

	constructor(
		private restaurantService: RestaurantService,
		private titleService: Title,
		private events: Events,
		private onesignalService: OnesignalService,
		private keen: KeenTrackingService,
		private orderFlowTrace: OrderFlowTraceService,
		private mpBannerTrace: MpBannerTraceService,
		private localStorageService: LocalStorageService,
		private orderService: OrderService,
		private restaurantLocalStorageService: RestaurantLocalStorageService,
		private recommendProductService: recommendProductService,
		private tableService: TableService,
		private cartService: ShoppingCartService,
		private navService: NavService,
		private wabotClientLastOrderService: WabotClientLastOrderService,
		private modalController: ModalController,
		private activatedRoute: ActivatedRoute,
		private scrollSaveService: ScrollSaveService,
		private ngZone: NgZone,
		private router: Router,
		private cdr: ChangeDetectorRef,
		@Inject(PLATFORM_ID) private platformId: Object,
	) { }

	// Por `inject()` e não pelo construtor: os specs montam esta página por posição.
	private readonly frescorDoHtml = inject(FrescorDoHtmlService);
	private readonly meta = inject(Meta);
	private readonly documento = inject(DOCUMENT);

	/**
	 * Carrossel de destaques do layout novo: as fotos depois da 1ª só ganham `src` quando ela chega
	 * (ou falha). Com todas saindo juntas elas dividiam a banda e a que aparece primeiro — o LCP —
	 * chegava por último. Mesma ideia do carrossel legado (`HeaderPage.destaquesLiberados`).
	 */
	destaquesLiberados = false;
	private readonly elDaPagina = inject(ElementRef);
	private readonly cdrDestaques = inject(ChangeDetectorRef);
	// Com SSR a 1ª foto pode ter chegado ANTES da hidratação, e o `(load)` do template não ouve o
	// que já aconteceu: depois do primeiro render, se ela já está completa, libera.
	private readonly liberarSeJaCarregou = afterNextRender(() => {
		const primeira: HTMLImageElement | null = this.elDaPagina.nativeElement.querySelector('.featured-carousel .highlight-image');
		if (primeira && primeira.complete && primeira.getAttribute('src')) this.liberarDestaques();
	});

	/** A 1ª foto do carrossel chegou (ou falhou): as outras podem carregar. */
	liberarDestaques(): void {
		if (this.destaquesLiberados) return;
		this.destaquesLiberados = true;
		this.cdrDestaques.markForCheck();
	}

	async ngOnInit() {
		if(!this.restaurantService.restaurant){
			// "Não deu tempo" não é "não existe". Os dois deixam `restaurant`
			// vazio, mas dizem coisas opostas ao cliente: `<app-not-found>` manda
			// embora de vez quem só está numa rede ruim. Ver `core.module.ts`.
			//
			// `invalidURL` continua ligado nos DOIS casos de propósito: é ele que
			// esconde carrinho, cashback e rodapé, e todos eles pressupõem um
			// restaurante. A flag nova só troca a MENSAGEM.
			this.invalidURL = true
			this.cargaExpirou = this.restaurantService.falhaDeCarga === 'prazo'
			return
		}

		// Essencial de render (roda também no SSR/prerender): dados do cardápio,
		// título (meta) e a montagem da lista de produtos.
		this.restaurant = this.restaurantService.restaurant;
		this.highlightImages = this.restaurant.info.highlightImages;
		this.setFooterLink();
		this.titleService.setTitle(this.restaurant.info.name);
		this.getProductsOfRestaurant();
		if (!isPlatformBrowser(this.platformId)) this.metaTagsDaLoja();

		// Loja bloqueada não mostra cardápio: é regra de EXIBIÇÃO, e por isso roda antes do guard
		// abaixo. Ficou do lado errado dele quando o SSR entrou (`ae946fb3`), e o HTML passou a sair
		// com o cardápio de quem está bloqueado — 5,2% das lojas com página no yellow em 21/09/2026,
		// cerca de 244. A chamada aqui também deixa o valor certo NO MOMENTO da hidratação: o
		// cliente monta o componente de novo com `restaurantIsBlocked = false`, e só o que corre
		// antes do primeiro render casa com o HTML do servidor.
		this.checkCanUseMenu();

		// Daqui p/ baixo é efeito browser-only (analytics, push, tracking, eventos,
		// recomendações, restauração de scroll, query params). No server é no-op;
		// o cliente executa na hidratação.
		if (!isPlatformBrowser(this.platformId)) return;

		// Cardápio vindo do HTML pré-renderizado: confere se ainda é o atual e, se não for,
		// recua para o render de hoje. No-op em todo boot sem SSR. Ver FrescorDoHtmlService.
		this.frescorDoHtml.conferir();

		this.keen.setEventBase().then(() => {
			this.keen.event('loadRestaurant');
		})

		// Abre o funil. É o primeiro evento da trilha; o ensureAttempt da sessão
		// é idempotente, então entrar por deep-link no carrinho (ou voltar de um
		// pagamento externo) não parte o funil em dois.
		this.orderFlowTrace.emit('ClientMenuViewed', {
			out: {
				products_count: (this.restaurant.info.products || []).length,
				categories_count: (this.restaurant.info.categories || []).length,
				cashier_is_open: !!this.restaurant.info.cashier_is_open,
				is_closed: !!this.restaurant.info.is_closed,
				menu_channel: 'web',
			},
		}, 'menu.ngOnInit');

		// LCP do cardápio, medido em campo. Agendado aqui e emitido só quando o
		// valor for final — ler agora devolveria LCP parcial, porque o evento
		// acima sai quando o DADO chega e as imagens ainda não pintaram.
		// `agendarRelatorioFinal` é idempotente por módulo: voltar ao cardápio
		// não gera um segundo evento.
		agendarRelatorioFinal((leitura) => {
			try {
				const rede: any = (navigator as any).connection
					|| (navigator as any).mozConnection || (navigator as any).webkitConnection;
				const out: any = { ...leitura, pref: false };
				if (rede) {
					if (typeof rede.effectiveType === 'string') out.net_type = rede.effectiveType.slice(0, 10);
					if (typeof rede.downlink === 'number' && isFinite(rede.downlink)) out.net_downlink = rede.downlink;
					if (typeof rede.saveData === 'boolean') out.save_data = rede.saveData;
				}
				try { out.route = (window.location.pathname || '').slice(0, 80); } catch (e) { }
				// De onde veio o HTML que acabou de pintar. Sem este campo, com o dial em 10% não há
				// como separar quem recebeu SSR de quem recebeu CSR DENTRO da mesma janela — sobraria
				// antes × depois, que é pior e mais lento. É a régua da Etapa 3.
				//
				// A fonte é o `data-mp-ssr` do `<html>`, que o servidor carimba ao serializar: ele fala
				// do DOCUMENTO que pintou, que é o que o LCP mede. O `htmlDoServidor` do
				// RestaurantService fala do cardápio, e os dois podem divergir se a TransferState
				// falhar.
				try {
					out.render = document.documentElement.getAttribute('data-mp-ssr') === '1' ? 'ssr' : 'csr';
					// Por que não foi SSR: o porteiro conta o motivo num Server-Timing (`sem-pagina`,
					// `validade`, `cardapio`, `dial`, `pedido`). Same-origin, então não precisa de
					// Timing-Allow-Origin. Quando não houver, o campo simplesmente não vem.
					const nav: any = performance.getEntriesByType('navigation')[0];
					const carimbo = (nav?.serverTiming || []).find((s: any) => s.name === 'mp-render');
					if (carimbo?.description) out.render_motivo = String(carimbo.description).slice(0, 24);
				} catch (e) { }
				this.orderFlowTrace.emit('ClientMenuRendered', { out }, 'menu.lcp');
			} catch (e) {
				// Régua que quebra não pode levar o cardápio junto.
			}
		});

		gtag('event', 'view_item_list', {
			'item_list_name': this.restaurant.info.name,
			'restaurant_id': this.restaurant.info.id,
		});

		this.showInstagramIosBanner = this.isInstagram() && this.isIOS();

		if (this.restaurant.info.fb_pixel_id) {
			fbq('init', this.restaurant.info.fb_pixel_id);
			fbq('track', 'PageView');
		}

		if (this.restaurant.info.use_push_notifications) {
			this.promptPush()
		}

		this.events.subscribe('onClickSameTab', () => {
			this.cancelPendingScroll();
			this.content.scrollToTop();
		})

		this.events.subscribe('reloadRestaurant', () => {
			this.loaded = false;
			this.categories = [];
			this.restaurantService.loadByURL(this.restaurant.info.restaurant_url).then((data) => {
				this.loaded = this.restaurantService.loaded;
				this.restaurant = this.restaurantService.restaurant;
				this.titleService.setTitle(this.restaurant.info.name);
				this.getProductsOfRestaurant();
			});
		})

		try {
			await this.loadRecommendedProducts();
		} catch (error) {
			console.error('Erro em loadRecommendedProducts:', error);
		}

		this.onContentAboveSettled();

		let repeatOrderUuid = this.activatedRoute.snapshot.queryParamMap.get('repeat-order-uuid');

		if (!repeatOrderUuid) {
			const queryString = window.location.search;
			const urlParams = new URLSearchParams(queryString);
			repeatOrderUuid = urlParams.get('repeat-order-uuid');
		}

		if (repeatOrderUuid) {
			setTimeout(async () => {
				await this.loadLastOrder(repeatOrderUuid);
			}, 800);
		}

	}

	private transformComboForCart(combo: any): any {
		if (!combo || combo.type !== 'combo') {
			return combo;
		}

		const transformedCombo = { ...combo };

		if (Array.isArray(transformedCombo.comboItems)) {
			transformedCombo.comboItems = transformedCombo.comboItems.map((comboItem: any) => {
				const transformedItem = { ...comboItem };

				if (Array.isArray(comboItem.flavors) && comboItem.flavors.length > 0) {
					const totalFlavorsSelectedCount = comboItem.flavors.reduce((total: number, flavor: any) => {
						return total + (Number(flavor.quantity) || 1);
					}, 0);

					transformedItem.selectedFlavors = comboItem.flavors.map((flavor: any) => {
						const transformedFlavor = { ...flavor };
						if (!transformedFlavor.fractionText && totalFlavorsSelectedCount > 0) {
							const qty = Number(flavor.quantity) || 1;
							const reducedFraction = this.cartService.reduceFraction([qty, totalFlavorsSelectedCount]);
							transformedFlavor.fractionText = `${reducedFraction[0]}/${reducedFraction[1]}`;
						}
						return transformedFlavor;
					});

					transformedItem.qttyOfFlavorsSelected = totalFlavorsSelectedCount;

					delete transformedItem.flavors;
				} else if (!transformedItem.selectedFlavors || !Array.isArray(transformedItem.selectedFlavors)) {
					transformedItem.selectedFlavors = [];
					transformedItem.qttyOfFlavorsSelected = 0;
				} else if (Array.isArray(transformedItem.selectedFlavors) && transformedItem.selectedFlavors.length > 0) {
					if (!transformedItem.qttyOfFlavorsSelected) {
						transformedItem.qttyOfFlavorsSelected = transformedItem.selectedFlavors.reduce((total: number, flavor: any) => {
							return total + (Number(flavor.quantity) || 1);
						}, 0);
					}
				}

				if (transformedItem.crust === null || transformedItem.crust === undefined) {
					transformedItem.crust = {};
				}
				if (transformedItem.dough === null || transformedItem.dough === undefined) {
					transformedItem.dough = {};
				}

				return transformedItem;
			});
		}

		if (!transformedCombo.totalPrice && transformedCombo.subTotal !== undefined) {
			transformedCombo.totalPrice = transformedCombo.subTotal;
		}
		if (!transformedCombo.qty && transformedCombo.quantity !== undefined) {
			transformedCombo.qty = transformedCombo.quantity;
		}

		return transformedCombo;
	}

	async loadLastOrder(uuid: string, attempt: number = 0) {
		const MAX_ATTEMPTS = 20;

		try {
			if (!this.restaurant || !this.restaurant.menu) {
				if (attempt >= MAX_ATTEMPTS) {
					console.warn('Restaurante ou menu não carregaram após várias tentativas, desistindo.');
					return;
				}
				console.warn('Restaurante ou menu não estão carregados ainda. Tentando novamente...');
				setTimeout(() => this.loadLastOrder(uuid, attempt + 1), 500);
				return;
			}

			if (this.categories.length === 0 && this.availableProducts.length === 0) {
				if (attempt >= MAX_ATTEMPTS) {
					console.warn('Menu não foi processado após várias tentativas, desistindo.');
					return;
				}
				console.warn('Menu ainda não foi processado. Aguardando...');
				setTimeout(() => this.loadLastOrder(uuid, attempt + 1), 500);
				return;
			}

			const order = await this.wabotClientLastOrderService.getOrderByUUID(uuid);
			const lastOrderItems = order?.items;

			if (!lastOrderItems || lastOrderItems.length === 0) {
				console.warn('Nenhum item encontrado no último pedido');
				return;
			}

			// O validador (20 KB minificados) chega SÓ aqui, por `import()`. Ele compara o último
			// pedido inteiro contra o cardápio e só roda quando a pessoa entra por um link de
			// recuperação de carrinho (o `uuid` do wabot) — caminho que a esmagadora maioria das
			// visitas nunca toma. Como `import type`, o tipo acima não puxa nada para o bundle.
			const { validateLastOrderAgainstMenu } = await import('src/app/core/services/menu-validators.service');
			const { validItems, removed } = validateLastOrderAgainstMenu(this.restaurant.menu, lastOrderItems as LastOrderItem[]);

			if (removed.length > 0) {
				console.warn('Alguns itens foram removidos do último pedido:', removed);
				await this.showRemovedItemsModal(removed, validItems.length > 0);
			}

			if (validItems.length === 0) {
				console.warn('Nenhum item válido encontrado no último pedido após validação');
				console.warn('Itens originais:', lastOrderItems);
				console.warn('Menu disponível:', {
					combos: this.restaurant.menu.combos.length,
					pizzas: this.restaurant.menu.pizzas.sizes.length,
					general: this.restaurant.menu.general.length
				});
				return;
			}

			const transformedItems = validItems.map((item: any) => {
				if (item.type === 'combo') {
					return this.transformComboForCart(item);
				}
				return item;
			});

			const cartItems = normalizeOrderForCart(transformedItems, {
				stripMenuItemId: true
			});

			this.cartService.clearCart();

			cartItems.forEach((item) => {
				this.cartService.addProductToCart(item);
			});

			setTimeout(() => {
				this.navService.push('shopping-cart');
			}, 500);

		} catch (error) {
			console.error('Erro ao carregar último pedido:', error);
			console.error('Stack trace:', error.stack);
		}
	}

	async showRemovedItemsModal(removedItems: Array<{ item: LastOrderItem; motivo: string }>, hasValidItems: boolean) {
		const modal = await this.modalController.create({
			component: RemovedItemsDialogPage,
			componentProps: {
				removedItems: removedItems,
				hasValidItems: hasValidItems
			},
			backdropDismiss: false,
			cssClass: 'modal-removedItems-dialog',
		});
		return await modal.present();
	}

	ngAfterViewInit() {
		// Restauração/observação de scroll depende do ion-content real: browser-only.
		if (!isPlatformBrowser(this.platformId)) return;
		// Relógio da permanência na página: a base de `ms_on_page` da telemetria
		// do banner. Aqui, e não no ngOnInit, para não contar o tempo de
		// prerender/hidratação como tempo de gente olhando.
		this.pageEnteredAt = performance.now();
		this.watchFooterBanner();
		this.content?.getScrollElement().then(el => {
			if (this.destroyed) return;
			this.scrollEl = el;
			this.listenToScroll(el);
			this.watchNavigation();
			const anchor = this.scrollSaveService.getAnchor(MenuPage.SCROLL_KEY);
			if (anchor && anchor.absoluteY > 0) this.restoreScroll(anchor);
		});
	}

	/**
	 * Fora da zone do Angular: eventos de scroll não devem disparar change detection global
	 * (em cardápios grandes isso reavalia todos os cards a cada evento e trava o scroll em
	 * dispositivos móveis). O onScroll() do new-home reentra na zone no settle.
	 *
	 * Toque, ponteiro e roda são GESTOS e cancelam a restauração da volta: pousar debaixo do
	 * dedo de quem já está mexendo na tela é pior do que não restaurar. O que rola sem gesto
	 * (teclado, barra de rolagem) é pego por `movedByScroll`.
	 *
	 * A pill NÃO é cancelada por gesto (ver `scrollToElement`): quem tocou numa categoria e
	 * não viu nada acontecer toca de novo, abre o select, arrasta a fileira de pills — e todos
	 * esses gestos passam por aqui. Só rolar de verdade a faz desistir do scroll final.
	 *
	 * O click que atravessa uma animação nossa e cai em outro ALVO clicável é engolido, na fase
	 * de captura, antes do handler do card (ver `isClickThrough`). Para isso o toque é seguido
	 * da descida à subida, e morre se virar rolagem.
	 */
	private listenToScroll(el: HTMLElement) {
		this.ngZone.runOutsideAngular(() => {
			const onScroll = () => {
				this.currentScrollTop = el.scrollTop;
				if (performance.now() > this.animationWindowEnd()) {
					// não é animação nossa: a pill que espera não pousa por cima de quem rolou, e o dedo
					// que estava na tela virou rolagem — rolagem não gera click, o toque morreu aqui
					if (this.pill) this.pill.sawScroll = true;
					this.press = null;
				}
				if (this.isNewHomePedirDelivery) {
					clearTimeout(this.scrollSettleTimer);
					this.scrollSettleTimer = setTimeout(() => {
						this.scrollSettleTimer = null;
						this.ngZone.run(() => this.onScroll());
					}, 100);
				}
			};
			const onGesture = () => this.endRestore();
			const gestures = ['touchstart', 'pointerdown', 'wheel'];
			// o toque só vale para o click enquanto está VIVO: a descida o arma; a subida marca o
			// relógio do click que vem atrás dela; o cancelamento (o dedo virou rolagem) o mata
			const presses = ['touchstart', 'pointerdown'];
			const releases = ['touchend', 'pointerup'];
			const cancels = ['touchcancel', 'pointercancel'];
			const onPress = (event: Event) => { this.press = { target: clickTargetOf(event.target), at: performance.now(), releasedAt: null }; };
			const onRelease = () => { if (this.press && this.press.releasedAt === null) this.press.releasedAt = performance.now(); };
			const onCancel = () => { this.press = null; };
			const onClick = (event: MouseEvent) => {
				const through = this.isClickThrough(event);
				const sinceTap = this.press ? performance.now() - this.press.at : 0;
				this.press = null;
				if (!through) return;
				event.preventDefault();
				event.stopPropagation();
				console.warn(`${MenuPage.SCROLL_LOG} clique ignorado: o toque desceu antes do fim da animação de scroll e o click caiu em outro alvo`);
				// A guarda é a cura de "toque repetido abre o produto errado"; este evento é a
				// vigilância dela. Volume muito acima do de pills = falso positivo, isto é,
				// toque legítimo sendo descartado.
				this.orderFlowTrace.emit('ClientMenuTapIgnored', {
					out: { origem: 'guarda_de_scroll', ms_desde_o_toque: Math.round(sinceTap) },
				}, 'MenuPage.isClickThrough');
			};
			const capture = { capture: true, passive: true };
			el.addEventListener('scroll', onScroll, { passive: true });
			gestures.forEach(type => el.addEventListener(type, onGesture, { passive: true }));
			presses.forEach(type => el.addEventListener(type, onPress, capture));
			releases.forEach(type => el.addEventListener(type, onRelease, capture));
			cancels.forEach(type => el.addEventListener(type, onCancel, capture));
			el.addEventListener('click', onClick, { capture: true });
			this.detachScrollListeners = () => {
				el.removeEventListener('scroll', onScroll);
				gestures.forEach(type => el.removeEventListener(type, onGesture));
				presses.forEach(type => el.removeEventListener(type, onPress, { capture: true }));
				releases.forEach(type => el.removeEventListener(type, onRelease, { capture: true }));
				cancels.forEach(type => el.removeEventListener(type, onCancel, { capture: true }));
				el.removeEventListener('click', onClick, { capture: true });
			};
		});
	}

	/**
	 * A âncora é capturada pelo roteador, enquanto o DOM ainda está de pé e visível: toda saída
	 * da página passa por ele. No `ngOnDestroy` é tarde — o router-outlet já desanexou a view —
	 * e no layout legado o `ion-router-outlet` esconde a página (`display:none`) antes.
	 *
	 * Duas vezes: no `NavigationStart` (vale sozinha se a navegação morrer antes, ex.:
	 * ChunkLoadError) e de novo no `ResolveEnd`, o último evento antes de o roteador desativar
	 * a rota. Entre os dois o módulo lazy do item carrega — segundos em rede ruim — com a página
	 * rolável; a volta tem de pousar onde a pessoa PAROU, não onde tocou.
	 */
	private watchNavigation() {
		this.navigationSub?.unsubscribe();
		this.navigationSub = this.router.events.subscribe(event => {
			if (event instanceof NavigationStart || event instanceof ResolveEnd) this.saveScrollAnchor();
		});
	}

	ionViewWillEnter() {
		if (this.availableProducts.length > 0) {
			this.loadRecommendedProducts();
		}
	}

	ngOnDestroy() {
		this.disconnectBannerObserver();
		// Sem `saveScrollAnchor()` aqui: a view já foi desanexada e a captura não mediria nada
		// (a âncora sai no roteador, ver `watchNavigation`).
		this.destroyed = true;
		this.cancelPendingScroll();
		clearTimeout(this.scrollSettleTimer);
		this.scrollSettleTimer = null;
		this.animationHolds.forEach(hold => { clearTimeout(hold.timer); hold.release(); });
		this.animationHolds.clear();
		this.navigationSub?.unsubscribe();
		this.navigationSub = null;
		this.detachScrollListeners?.();
		this.detachScrollListeners = null;
		this.events.destroy('onClickSameTab');
		this.events.destroy('reloadRestaurant');
	}

	checkCanUseMenu() {
		// No servidor não existe mesa: a URL do render não tem `?t=` e não há localStorage. A conta
		// cai em `!blocked && digital_menu`, que é o que decide para quem chega pelo link da loja.
		// Quem entra por QR de mesa revalida na hidratação, no navegador, com os dois valores.
		const noNavegador = isPlatformBrowser(this.platformId);
		const tableFromUrl = noNavegador
			? this.tableService.decodeTableFromUrl(new URLSearchParams(window.location.search).get('t'))
			: null;
		const tableFromLocalStorage = noNavegador
			? JSON.parse(localStorage.getItem(`table-${this.restaurant.info.id}`))
			: null;

		const isBlocked = this.restaurant.info.blocked;
		const hasDigitalMenu = this.restaurant.info.modules.digital_menu;
		const hasSimplePos = this.restaurant.info.modules.simple_pos;
		const hasPos = this.restaurant.info.modules.pos;
	
		this.restaurantIsBlocked = true;
	
		const activeToTable = (hasPos || hasSimplePos) && (tableFromUrl || tableFromLocalStorage);
		const activeToDigitalMenu = hasDigitalMenu;

		if((tableFromUrl || tableFromLocalStorage) && !hasPos && !hasSimplePos && hasDigitalMenu) {
			this.restaurantIsBlocked = true;
			return;
		}
		
		if (!isBlocked && (activeToDigitalMenu || activeToTable)) {
			this.restaurantIsBlocked = false;
		}
	}

	/** Último termo pedido — a resposta que chegar fora de ordem é descartada por ele. */
	private ultimoTermoBuscado = '';

	// TODO: remove this when new pedir delivery is on 100% of the clients
	onNewSearchInput(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		if (this.searchDebounceTimer !== undefined) clearTimeout(this.searchDebounceTimer);
		this.ultimoTermoBuscado = value;
		if (!value.trim()) {
			this.searchResults = this.restaurant.menu;
			return;
		}
		this.searchDebounceTimer = setTimeout(async () => {
			// `filterMenu` virou assíncrono porque o minisearch só é baixado na primeira busca
			// (ver restaurant.service). A guarda depois do await evita que um resultado antigo
			// sobrescreva um mais novo se duas buscas se cruzarem.
			const resultados = await this.restaurantService.filterMenu(value, this.restaurant.menu);
			if (this.ultimoTermoBuscado !== value) return;
			this.searchResults = resultados;
		}, 300);
	}

	private get showStockPaused(): boolean {
		const info = this.restaurant?.info;
		if (info?.modules?.ingredient_inventory) {
			return info?.supply_stock_config?.display_paused_items_as_sold_out ?? false;
		}
		return info?.stock_configuration?.display_paused_items_as_sold_out ?? false;
	}

	private isProductUnavailable(product: MenuItem): boolean {
		return product.unavailable || product.unavailableByTemplate;
	}

	private isProductInStock(product: MenuItem): boolean {
		return product.hasStock !== false || this.showStockPaused;
	}

	getProductsOfRestaurant() {
		this.searchResults = this.restaurant.menu;

		const hasVisibleItem = (item: MenuItem) => !this.isProductUnavailable(item) && this.isProductInStock(item);

		this.itemsToHide.featuredItems = !this.restaurant.menu.featuredItems.some(hasVisibleItem);

		this.itemsToHide.combos = true;
		this.restaurant.menu.combos.forEach(combo => {
			if (hasVisibleItem(combo)) {
				this.itemsToHide.combos = false;
				this.availableProducts.push(combo);
			}
		});

		this.itemsToHide.pizzas = true;
		this.restaurant.menu.pizzas.sizes.forEach(pizzas => {
			if (hasVisibleItem(pizzas)) {
				this.itemsToHide.pizzas = false;
				this.availableProducts.push(pizzas);
			}
		});

		this.restaurant.menu.general.forEach(category => {
			// `available` é só o liga/desliga manual: categoria fora do horário do template
			// chega com available=true e unavailableByTemplate=true, e a pill aparecia no
			// nav enquanto a listagem (menu-list buildCategoryVM) escondia a categoria.
			if (!category.available || category.unavailableByTemplate)
				return;

			let filteredProducts = category.products.filter(product => {
				if (this.isProductUnavailable(product)) return false;
				if (!this.isProductInStock(product)) return false;
				return true;
			});

			if(filteredProducts.length > 0) {
				this.availableProducts.push(...filteredProducts);
				this.categories.push(category);
			}
		});
	}

	/**
	 * `og:title`, `og:image` e o ícone da aba com o nome e a foto da loja. Só no servidor.
	 *
	 * Paridade com o Lambda@Edge `cloudfront_origin_response_pedirdelivery_meta_seo`, que injeta as
	 * mesmas três tags no index de produção: sem elas, o link da loja colado no WhatsApp mostraria
	 * "Pedir Delivery" e o ícone genérico. Quem lê é o robô de pré-visualização, que não executa JS,
	 * então no navegador elas não mudariam nada.
	 */
	private metaTagsDaLoja() {
		this.meta.updateTag({ property: 'og:title', content: this.restaurant.info.name });
		const foto = this.restaurantProfilePicUrl;
		if (!foto) return;
		this.meta.updateTag({ property: 'og:image', content: foto });
		// O ícone quem baixa é o NAVEGADOR do visitante, não o robô: com o JPEG original ele puxava o
		// logo inteiro (pastel.com: 409 KB, medido no yellow em 22/09/2026 — metade das imagens da
		// página). Vai a versão de 240 px; o og:image continua JPEG para o robô.
		const icone = this.documento.querySelector('link[rel="icon"]');
		if (icone) {
			const versao = this.avatarDaLoja;
			icone.setAttribute('href', versao);
			// O index declara `image/png`; o navegador pode recusar um ícone de tipo declarado errado.
			icone.setAttribute('type', versao.endsWith('.webp') ? 'image/webp' : 'image/jpeg');
		}
	}

	/** O JPEG original: é o que vai no `og:image` e no ícone, lidos pelo robô de pré-visualização. */
	get restaurantProfilePicUrl(): string {
		const pic = this.restaurant?.info?.profile_pic;
		if (!pic) return null;
		return urlOriginal(caminhoDoLogo(pic));
	}

	/** O logo para a TELA (avatar do topo fixo, 40 px): a versão de 240 px. */
	get avatarDaLoja(): string {
		const pic = this.restaurant?.info?.profile_pic;
		return pic ? urlDaVersao(caminhoDoLogo(pic), 240) : null;
	}

	/** Destaque do carrossel (160 px na tela): a `lg_` tem 600 px, então 640 só troca o formato. */
	urlDoDestaque(imagem: string): string {
		return urlDaVersao(caminhoDoDestaque(imagem), 640);
	}

	originalDoDestaque(imagem: string): string {
		return urlOriginal(caminhoDoDestaque(imagem));
	}

	/**
	 * Pill do category-nav.
	 *
	 * Categoria ainda não montada pelo render progressivo: pede ao pump só ATÉ ela e uma tela
	 * abaixo, nunca `renderAllNow()`. Com o pump suspenso a task que alcança o alvo para nele,
	 * e enquanto a pessoa espera parada o alvo é URGENTE: as tasks sobem ao orçamento de resposta
	 * (`MenuListPage.URGENT_TASK_BUDGET_MS`, 100 ms) em vez dos 40 ms do pump de fundo, sempre
	 * com o teto de 96 produtos por task. Pelo invariante do prefixo, quando a âncora existe
	 * tudo acima está completo e o topo dela no lugar final.
	 * Antes de animar, garante que o alvo CABE e que a tela nele é cardápio
	 * (`growUntilReachable`), e segura o pump durante a animação.
	 *
	 * O que desiste da espera:
	 *   - outra pill, "voltar ao topo", a página destruída;
	 *   - a pessoa ROLAR de verdade enquanto espera — só o scroll final é cancelado; o que já
	 *     montou fica, e desde o 1º scroll a montagem segue nas tasks do pump de fundo, que não
	 *     travam quem está rolando.
	 * Toque, ponteiro e roda que não rolam NÃO cancelam: abrir o select, arrastar a fileira de
	 * pills e, principalmente, tocar de novo no mesmo alvo — no select o mesmo valor não emite
	 * nada, então o toque repetido só cancelava. Um novo pedido do mesmo alvo (pill do layout
	 * novo, ou o segmento do legado, que nesta branch é um `<button>` cujo `(click)` sempre emite)
	 * recomeça a espera de onde o pump está: nada do que montou se perde. O click desse toque que chega
	 * depois de a animação começar não abre o card que passou debaixo do dedo (`isClickThrough`).
	 *
	 * Desistir (sem ter sido substituída por outro pedido) devolve o nav à categoria em que a
	 * página está (`showCategoryInView`).
	 */
	async scrollToElement(categoryElementID) {
		this.cancelPendingScroll();
		const intent = this.scrollIntent;
		if (categoryElementID === 'scroll-to-top') {
			const ended = this.holdPumpDuringAnimation(600);
			this.untilAnimationEnds(this.content.scrollToTop(600), ended);
			return;
		}
		const stillWanted = () => intent === this.scrollIntent && !this.destroyed;
		const list = this.menuList;
		const release = list?.suspendProgressiveRender();
		let pill: PendingPill | null = null;
		// A pill que encontrou a âncora já montada não esperou o pump: separar as duas
		// populações é o que diz se a espera vem do pump ou do scroll em si.
		let esperouPump = false;
		try {
			const scrollElement = this.scrollEl ?? await this.content.getScrollElement();
			if (!stillWanted()) return;

			const waiting: PendingPill = { elementId: categoryElementID, intent, seen: this.snapshot(this.listReference(), scrollElement), sawScroll: false, since: performance.now() };
			pill = this.pill = waiting;
			const urgent = () => stillWanted() && !waiting.sawScroll;
			const mountedBefore = list?.renderedProductCount ?? 0;
			let element = document.getElementById(categoryElementID);
			esperouPump = !element;
			if (!element) {
				element = await this.materializeCategoryAnchor(categoryElementID, scrollElement, stillWanted, urgent);
				if (!element) {
					if (stillWanted()) this.showCategoryInView();
					this.jumpOutcome = this.jumpOutcome || 'sem_ancora';
					return;
				}
			}
			const target = () => this.scrollOffsetOf(element, scrollElement) - MenuPage.CATEGORY_TOP_GAP;
			if (await this.growUntilReachable(scrollElement, target, stillWanted, stillWanted, urgent) === 'cancelled') {
				this.jumpOutcome = 'cancelou';
				return;
			}
			// Só quem esperou o pump pode ter sido atropelado por um scroll no meio. Decide na hora
			// e pelo scrollTop (`movedByScroll`), não pelo evento `scroll`: o evento só é despachado
			// no frame seguinte, e o scroll feito logo antes da última task ainda não tinha evento
			// aqui; esperar o frame é mais um frame de pouso, e ninguém ganha com ele. O evento só é
			// exigido se uma animação NOSSA rodou durante a espera (o scrollTop pode ter mudado por
			// ela). O gesto que a pessoa começou DURANTE a última task só chega à main thread depois
			// disto — no máximo uma task urgente (~100 ms) depois; o click dele que cair em outro
			// alvo é engolido (`isClickThrough`).
			const ourAnimationMoved = this.animationWindowEnd() > waiting.since;
			if ((list?.renderedProductCount ?? 0) !== mountedBefore && (waiting.sawScroll || !ourAnimationMoved) && this.movedByScroll(waiting.seen, scrollElement)) {
				console.warn(`${MenuPage.SCROLL_LOG} pill "${categoryElementID}": a pessoa rolou enquanto esperava; a categoria montou, o scroll final não sai`);
				this.showCategoryInView();
				this.jumpOutcome = 'cancelou';
				return;
			}

			// a suspensão da animação entra ANTES de soltar a da espera: o pump não respira no meio
			const ended = this.holdPumpDuringAnimation(MenuPage.CATEGORY_SCROLL_MS);
			this.untilAnimationEnds(this.content.scrollToPoint(0, target(), MenuPage.CATEGORY_SCROLL_MS), ended);
		} finally {
			release?.();
			if (pill && this.pill === pill) this.pill = null;
			if (pill) {
				this.orderFlowTrace.emit('ClientCategoryJumpSettled', {
					out: {
						desfecho: this.jumpOutcome || 'pousou',
						// o que a pessoa espera olhando a tela: a troca aceita foi "chega depois"
						ms: Math.round(performance.now() - pill.since),
						produtos_montados: this.menuList?.renderedProductCount ?? 0,
						esperou_pump: esperouPump,
					},
				}, 'MenuPage.scrollToCategory');
			}
			this.jumpOutcome = '';
		}
	}

	/** Monta até a âncora da categoria da pill existir, com uma tela de conteúdo abaixo dela, e a devolve. */
	private async materializeCategoryAnchor(elementId: string, scrollElement: HTMLElement, stillWanted: () => boolean, urgent: () => boolean): Promise<HTMLElement | null> {
		const list = this.menuList;
		const category = (this.categories || []).find(item => `${item.name}-${item.id}` === elementId);
		const index = category && list ? list.categoryIndexOf(category.id) : -1;
		if (index < 0) {
			// ex.: a busca do layout legado trocou a lista e a categoria não está no resultado
			console.warn(`${MenuPage.SCROLL_LOG} pill "${elementId}": categoria fora da lista atual, nada a rolar`);
			this.jumpOutcome = 'fora_da_lista';
			return null;
		}
		const point = list.positionAfter(index, this.screenOfProducts(scrollElement));
		const reached = await list.materializeThrough(point.index, point.products, stillWanted, urgent);
		if (!stillWanted()) return null;
		const element = document.getElementById(elementId);
		if (!reached || !element) {
			console.warn(`${MenuPage.SCROLL_LOG} pill "${elementId}": a âncora não apareceu (materializeThrough=${reached})`);
			return null;
		}
		return element;
	}

	/** Produtos que SUPOSTAMENTE enchem `px` a mais que uma tela (card suposto pequeno: pedir a mais só adianta o pump). */
	private screenOfProducts(scrollElement: HTMLElement, px = 0): number {
		return Math.ceil((Math.max(0, px) + scrollElement.clientHeight) / MenuPage.GROW_PX_PER_PRODUCT) + 1;
	}

	/**
	 * Cresce a lista até `target()` ser alcançável E a tela inteira, pousada nele, ser
	 * CARDÁPIO: a última categoria montada tem de passar do pé da viewport. Crescer só até o
	 * alvo caber em `scrollHeight - clientHeight` deixava o rodapé (que fica dentro do
	 * `ion-content`) logo abaixo da lista: no frame do pouso ele aparecia onde deveria estar a
	 * categoria seguinte, e o IntersectionObserver do banner contava uma impressão falsa (~2%
	 * das posições da 8466, até 23% nas categorias pequenas). Com o cardápio inteiro montado
	 * vale só o alcance: o rodapé no fim é o rodapé de verdade.
	 *
	 * Pede ao pump os produtos que faltam a partir da fronteira do que já existe — a última
	 * categoria iniciada, a única que pode estar parcial —, então todo crescimento entra
	 * ABAIXO do alvo e não o move.
	 *
	 * `stillWanted` é conferido a cada crescimento; `wanted` (sem efeito colateral e sem ler
	 * layout, porque roda dentro das tasks) é o abort que vai ao pump, e `urgent` idem.
	 *
	 * `'cancelled'`: `stillWanted()` virou falso. `'reachable'`: o alvo cabe. `'end'`: o
	 * cardápio inteiro já está montado e o alvo passa do fim — o browser vai truncar. Para
	 * uma pill das últimas categorias isso é o normal (elas não sobem até o topo); para a
	 * volta de um item é degradação, e quem chama decide se avisa.
	 */
	private async growUntilReachable(scrollElement: HTMLElement, target: () => number, stillWanted: () => boolean, wanted = stillWanted, urgent?: () => boolean): Promise<'reachable' | 'end' | 'cancelled'> {
		const list = this.menuList;
		for (;;) {
			const reach = target() - (scrollElement.scrollHeight - scrollElement.clientHeight);
			const deficit = Math.max(reach, this.coverageDeficit(scrollElement, target(), list));
			if (deficit <= 1) return 'reachable';
			const next = list ? this.nextGrowthTarget(list, deficit) : null;
			if (!next) return reach <= 1 ? 'reachable' : 'end';
			const reached = await list.materializeThrough(next.index, next.products, wanted, urgent);
			if (!stillWanted()) return 'cancelled';
			if (!reached) {
				console.warn(`${MenuPage.SCROLL_LOG} o pump recusou crescer até a categoria ${next.index}; o destino pode ser truncado`);
				return 'end';
			}
		}
	}

	/** Px de lista que faltam abaixo da tela pousada em `target` (≤ 0: coberta). 0 com o cardápio inteiro montado. */
	private coverageDeficit(scrollElement: HTMLElement, target: number, list: MenuListPage | undefined): number {
		if (!list || list.isFullyRendered) return 0;
		const views = list.generalView;
		const last = views[views.length - 1];
		const node = last ? document.getElementById(`${last.category.name}-${last.category.id}`) : null;
		if (!node) return 0;
		const listEnd = node.getBoundingClientRect().bottom - scrollElement.getBoundingClientRect().top + scrollElement.scrollTop;
		return target + scrollElement.clientHeight - listEnd;
	}

	/** Próximo alvo de crescimento: `deficit` px convertidos em produtos a partir da fronteira. */
	private nextGrowthTarget(list: MenuListPage, deficit: number): { index: number; products: number } | null {
		const views = list.generalView;
		const total = list.menu?.general?.length ?? 0;
		const more = Math.max(1, Math.ceil(deficit / MenuPage.GROW_PX_PER_PRODUCT));
		const last = views[views.length - 1];
		if (last && last.materialized < last.allProducts.length) {
			return { index: views.length - 1, products: last.materialized + more };
		}
		return views.length < total ? { index: views.length, products: more } : null;
	}

	/**
	 * O click atravessou uma animação de scroll NOSSA e caiu em OUTRO alvo? O dedo desceu num (a
	 * pill, o segmento), a animação andou, e o browser entrega o click no que passou debaixo do
	 * dedo — um card, que abria um produto que ninguém escolheu ou punha um no carrinho. Com a
	 * pill esperando o pump isso é o toque repetido: o touchstart sai com a página parada, e o
	 * click, depois do frame que renderiza os cards e dá o 1º passo da animação. Um scroll de
	 * verdade debaixo do dedo não gera click; a animação nossa gera.
	 *
	 * Só engole com PROVA de que o toque atravessou para outro alvo, e quando tudo vale:
	 *   - o click veio de ponteiro (`detail` > 0: teclado e `.click()` programático passam);
	 *   - de um toque VIVO: o dedo ainda na tela, ou a subida há até `CLICK_AFTER_RELEASE_MS`. O
	 *     arrasto que virou rolagem não deixa toque armado (ver `listenToScroll`);
	 *   - a janela de uma animação nossa alcança a DESCIDA — desceu antes ou durante ela, contada
	 *     até o fim real (`animationWindowEnd`), não até o click;
	 *   - o ALVO CLICÁVEL do click (`clickTargetOf`) existe e é outro que o da descida, sem um
	 *     conter o outro. Descer no nome e soltar no preço do MESMO card passa; descer na pill e
	 *     soltar num card, não. Click que não aciona nada passa: não há o que proteger.
	 *
	 * O alvo da descida é medido NA descida: um nó de dentro do card recriado depois não conta.
	 * Se o próprio alvo saiu do DOM (a fileira de pills vira select quando a barra gruda), a
	 * identidade não prova mais nada, e só engole o click que caiu num card vindo de uma descida
	 * fora de card — uma pill que sumiu não vira produto; um card recriado pode ser o mesmo.
	 */
	private isClickThrough(event: MouseEvent): boolean {
		const press = this.press;
		if (!press || !event.detail) return false;
		if (press.releasedAt !== null && performance.now() - press.releasedAt > MenuPage.CLICK_AFTER_RELEASE_MS) return false;
		if (this.animationWindowEnd() < press.at) return false;
		const up = clickTargetOf(event.target);
		const down = press.target;
		if (!up || up === down) return false;
		if (!down) return true;
		if (down.contains(up) || up.contains(down)) return false;
		if (!down.isConnected) return up.localName === 'card' && down.localName !== 'card';
		return true;
	}

	/**
	 * Até quando uma animação de scroll nossa está (ou pode estar) andando: os eventos de scroll
	 * dela não são da pessoa, e o toque que desce antes disso pode ter o click atravessado. A
	 * animação cujo prazo passou sem o fim chegar é dada por terminada NO prazo.
	 */
	private animationWindowEnd(): number {
		const now = performance.now();
		let end = this.animationsSettledUntil;
		this.animationsInFlight.forEach(animation => {
			end = Math.max(end, animation.until);
			if (animation.until >= now) return;
			this.animationsInFlight.delete(animation);
			this.animationsSettledUntil = Math.max(this.animationsSettledUntil, animation.until);
		});
		return end;
	}

	/**
	 * Segura o pump durante uma animação de scroll (contado: animações sobrepostas não se soltam)
	 * e abre a janela dela. Devolve o aviso do fim REAL, que `untilAnimationEnds` liga à promessa
	 * do `scrollToPoint`/`scrollToTop`.
	 *
	 * A janela vai até o fim real, não até `durationMs` contados daqui: o Ionic ainda espera o
	 * elemento e um frame antes de começar a contar a duração, e logo depois de uma montagem esse
	 * frame é caro — o movimento terminava 300-400 ms depois da janela, e o toque que descia nesse
	 * trecho não era guardado. Nunca fecha antes de `durationMs` + folga, e nunca fica aberta mais
	 * de `ANIMATION_MAX_LAG_MS` além disso se o fim não chegar.
	 *
	 * A suspensão do pump continua solta em `durationMs` + folga, como antes.
	 */
	private holdPumpDuringAnimation(durationMs: number): () => void {
		const now = performance.now();
		const nominalEnd = now + durationMs + MenuPage.ANIMATION_TAIL_MS;
		const animation = { until: nominalEnd + MenuPage.ANIMATION_MAX_LAG_MS };
		this.animationsInFlight.add(animation);
		this.animationsSettledUntil = Math.max(this.animationsSettledUntil, nominalEnd);
		const ended = () => {
			if (!this.animationsInFlight.delete(animation)) return;
			this.animationsSettledUntil = Math.max(this.animationsSettledUntil, Math.min(animation.until, performance.now() + MenuPage.ANIMATION_TAIL_MS));
		};
		const release = this.menuList?.suspendProgressiveRender();
		if (!release) return ended;
		this.ngZone.runOutsideAngular(() => {
			const hold = {
				release,
				timer: setTimeout(() => {
					this.animationHolds.delete(hold);
					release();
				}, durationMs + MenuPage.ANIMATION_TAIL_MS),
			};
			this.animationHolds.add(hold);
		});
		return ended;
	}

	/** Liga o fim real da animação (a promessa do Ionic) à janela dela. Fora da zone: não é motivo de change detection. */
	private untilAnimationEnds(animation: unknown, ended: () => void) {
		this.ngZone.runOutsideAngular(() => { Promise.resolve(animation).then(ended, ended); });
	}

	/**
	 * A pill desistiu — a pessoa rolou enquanto esperava, ou a categoria não montou: o nav volta a
	 * marcar a categoria em que a página ESTÁ. O toque marcou a escolhida (a pill destacada, a
	 * opção no `<select>`), e só o scrollspy a corrigia, e só quando o VALOR dele mudava: quem
	 * rolou dentro da mesma categoria ficava com a categoria cancelada marcada — e no `<select>`
	 * escolher a mesma opção de novo não dispara `change`, então o destino ficava inalcançável
	 * até escolher outra antes.
	 *
	 * Só no layout novo. No legado o segmento se marca pelo TOQUE (`selectCategory` no `(click)`
	 * do `<button>`, sem Ionic): o listener de scroll não chama o `onScroll` do legado, então
	 * marcar pela posição só na desistência seria um comportamento que ele não tem em mais
	 * lugar nenhum. E tocar de novo no segmento marcado já emite.
	 */
	private showCategoryInView() {
		if (!this.isNewHomePedirDelivery) return;
		this.currentCategoryValue = this.categoryInView();
		this.categoryNav?.showActive(this.currentCategoryValue);
	}

	/** Posição do nó em coordenadas de SCROLL (a mesma convenção para pill, captura e volta). */
	private scrollOffsetOf(node: HTMLElement, scrollElement: HTMLElement): number {
		return node.getBoundingClientRect().top - scrollElement.getBoundingClientRect().top + scrollElement.scrollTop;
	}

	/** O topo da lista (acima dele: cabeçalho, carrossel de recomendações, barra das pills). */
	private listReference(): HTMLElement | null {
		return this.menuList?.listElement ?? null;
	}

	private snapshot(reference: HTMLElement | null, scrollElement = this.scrollEl): ScrollSnapshot {
		const el = scrollElement;
		return {
			top: el ? el.scrollTop : 0,
			reference,
			referenceTop: el && reference ? reference.getBoundingClientRect().top - el.getBoundingClientRect().top : 0,
		};
	}

	/**
	 * A página foi ROLADA desde `seen`? Rolar muda o `scrollTop` e move o nó de referência na
	 * tela; ajuste do browser muda só um dos dois (ver `ScrollSnapshot`). Sem referência
	 * medível, qualquer mudança de `scrollTop` conta.
	 */
	private movedByScroll(seen: ScrollSnapshot, scrollElement = this.scrollEl): boolean {
		const el = scrollElement;
		if (!el) return true;
		if (Math.abs(el.scrollTop - seen.top) <= 1) return false;
		const reference = seen.reference;
		if (!reference || !reference.isConnected) return true;
		const referenceTop = reference.getBoundingClientRect().top - el.getBoundingClientRect().top;
		return Math.abs(referenceTop - seen.referenceTop) > 1;
	}

	/** Scroll programático novo (pill, "voltar ao topo") ou a página saindo: quem esperava o pump para rolar desiste. */
	private cancelPendingScroll() {
		this.scrollIntent++;
		this.pill = null;
		this.endRestore();
	}

	private endRestore() {
		const restore = this.restore;
		if (!restore) return;
		this.restore = null;
		// Encerrar sem ter pousado é cancelamento: a pessoa rolou, tocou numa pill ou
		// navegou. Não é defeito — mas a proporção disso é que diz se a volta chega tarde
		// demais para valer a pena.
		if (!restore.landed) this.reportRestore(restore, 'cancelou', null);
		restore.releasePump?.();
		restore.releasePump = null;
	}

	/**
	 * Desfecho da volta. Um por restauração, exceto o 2º pouso (quando o carrossel de
	 * recomendações assenta acima da lista e a página pousa de novo pela âncora), que sai
	 * com `repouso: true` — é um desfecho diferente do primeiro e some se for agrupado.
	 */
	private reportRestore(restore: ScrollRestore, desfecho: string, desvio: number | null) {
		const repouso = restore.settled;
		if (repouso && desfecho === 'cancelou') return; // já relatado ao pousar
		restore.settled = true;
		this.orderFlowTrace.emit('ClientMenuRestoreSettled', {
			out: {
				desfecho,
				motivo: restore.motivo,
				ms: Math.round(performance.now() - restore.since),
				desvio_px: desvio === null ? null : Math.round(desvio),
				produtos_montados: this.menuList?.renderedProductCount ?? 0,
				repouso,
			},
		}, 'MenuPage.restoreScroll');
	}

	/**
	 * Descreve a posição atual como ÂNCORA: a última categoria cujo topo está no topo da
	 * viewport ou acima dele, e quanto dela já passou. Acima da 1ª categoria (destaques, combos,
	 * pizzas), e também logo acima da lista com ela ainda na tela (a pessoa vê a barra sticky e
	 * o começo da lista), a âncora é o topo da LISTA (`listOffset`): o carrossel de
	 * recomendações e a barra ficam acima dela e mudam de altura entre a saída e a volta. Só
	 * com a lista inteira abaixo da tela (cabeçalho, carrossel) vale o pixel.
	 *
	 * `null` quando não há o que medir. Sem caixa de layout — página escondida pelo
	 * `ion-router-outlet` (`display:none`) ou view já desanexada na destruição — rects e
	 * `scrollTop` leem 0, e "medir" ali produzia uma âncora falsa por cima da boa (rodada 1).
	 */
	private captureScrollAnchor(): ScrollAnchor | null {
		const el = this.scrollEl;
		if (!el || !el.isConnected || el.clientHeight === 0) return null;

		const y = el.scrollTop;
		const reference = this.listReference();
		const listTop = reference && reference.isConnected ? this.scrollOffsetOf(reference, el) : null;
		const listOffset = listTop !== null && y > 0 && listTop - y < el.clientHeight ? y - listTop : null;
		const hero: ScrollAnchor = { categoryId: null, categoryIndex: -1, offset: y, absoluteY: y, listOffset };
		const views = this.menuList?.generalView || [];
		if (y <= 0 || !views.length) return hero;

		let anchor: ScrollAnchor | null = null;
		for (let i = 0; i < views.length; i++) {
			const category = views[i].category;
			const node = document.getElementById(`${category.name}-${category.id}`);
			// view-model à frente do DOM (change detection pendente): o que falta está abaixo
			if (!node) break;
			const top = this.scrollOffsetOf(node, el);
			if (top > y) break;
			anchor = { categoryId: String(category.id), categoryIndex: i, offset: y - top, absoluteY: y, listOffset };
		}
		return anchor ?? hero;
	}

	/** Grava a âncora atual. Sem medida, NÃO grava: a última âncora boa continua valendo. */
	private saveScrollAnchor() {
		// Restauração que ainda não pousou e que ninguém interrompeu: a pessoa nunca viu outra
		// posição, então a âncora pendente continua sendo a verdade (ex.: o "repetir pedido"
		// navega sozinho para o carrinho no meio da volta).
		if (this.restore && !this.restore.landed) return;
		const anchor = this.captureScrollAnchor();
		if (anchor) this.scrollSaveService.saveAnchor(MenuPage.SCROLL_KEY, anchor);
	}

	/**
	 * Volta para onde a pessoa estava SEM montar o cardápio inteiro.
	 *
	 * A posição exige o conteúdo ATÉ ela: monta até a categoria da âncora e uma tela abaixo
	 * (pump suspenso, em tasks limitadas), mede onde ela ficou, cresce até a tela pousada ser
	 * cardápio, põe a barra sticky no estado que ela terá lá e pousa com `scrollTop` direto
	 * (sem animação: nada se move durante o pouso). Depois disso o pump é liberado e o resto
	 * chega ABAIXO.
	 *
	 * A janela até o pouso são várias tasks com a página já pintada e rolável, então
	 * interação cancela: gesto (touch/pointer/wheel), pill, "voltar ao topo", ou a página
	 * rolada por teclado/barra (`movedByScroll`). Pousar por cima de quem já está rolando é
	 * pior do que não restaurar.
	 */
	private async restoreScroll(anchor: ScrollAnchor): Promise<void> {
		const el = this.scrollEl;
		const list = this.menuList;
		if (!el || !list || this.destroyed) return;
		this.endRestore();
		const restore: ScrollRestore = {
			anchor,
			intent: ++this.scrollIntent,
			seen: this.snapshot(this.listReference()),
			landed: false,
			releasePump: list.suspendProgressiveRender(),
			since: performance.now(),
			motivo: '',
			settled: false,
		};
		this.restore = restore;
		try {
			await this.land(restore);
		} catch (error) {
			// restauração de scroll nunca derruba a tela
			console.warn(`${MenuPage.SCROLL_LOG} restauração abortada por erro`, error);
			restore.motivo = 'erro';
			this.reportRestore(restore, 'degradou', null);
			if (this.restore === restore) this.endRestore();
		}
	}

	/** Ninguém cancelou a restauração (sem ler layout: é o abort que o pump consulta nas tasks). */
	private isRestoreCurrent(restore: ScrollRestore): boolean {
		return !this.destroyed && this.restore === restore && restore.intent === this.scrollIntent;
	}

	/** A restauração ainda vale — ninguém cancelou e ninguém rolou? Se não, encerra (e libera o pump). */
	private restoreStillValid(restore: ScrollRestore): boolean {
		const valid = this.isRestoreCurrent(restore) && !!this.scrollEl && !this.movedByScroll(restore.seen);
		if (!valid && this.restore === restore) this.endRestore();
		return valid;
	}

	/** Um pouso (o primeiro, ou o de novo depois que o conteúdo de cima assentou). */
	private async land(restore: ScrollRestore): Promise<void> {
		const el = this.scrollEl;
		const list = this.menuList;
		const { anchor } = restore;

		let node: HTMLElement | null = null;
		if (anchor.categoryId !== null) {
			const index = list.categoryIndexOf(anchor.categoryId);
			if (index < 0) {
				console.warn(`${MenuPage.SCROLL_LOG} categoria ${anchor.categoryId} da âncora não existe no cardápio atual; pousando ${this.fallbackLabel(anchor)}`);
				restore.motivo = 'categoria_ausente';
			} else {
				// a categoria da âncora até uma tela abaixo do ponto: não a categoria inteira (DOCES tem 419)
				const point = list.positionAfter(index, this.screenOfProducts(el, anchor.offset));
				const reached = await list.materializeThrough(point.index, point.products, () => this.isRestoreCurrent(restore));
				if (!this.restoreStillValid(restore)) return;
				const category = list.menu.general[index];
				node = reached ? document.getElementById(`${category.name}-${category.id}`) : null;
				if (!node) {
					console.warn(`${MenuPage.SCROLL_LOG} âncora da categoria ${anchor.categoryId} não apareceu (materializeThrough=${reached}); pousando ${this.fallbackLabel(anchor)}`);
					restore.motivo = 'ancora_ausente';
				}
			}
		}
		// O conteúdo acima da lista já mudou nesta montagem: renderiza antes de medir.
		if (this.contentAboveSettled) this.cdr.detectChanges();

		const listRef = this.listReference();
		const target = () => {
			if (node) return this.scrollOffsetOf(node, el) + anchor.offset;
			if (anchor.listOffset != null && listRef) return this.scrollOffsetOf(listRef, el) + anchor.listOffset;
			return anchor.absoluteY;
		};
		const reach = await this.growUntilReachable(el, target, () => this.restoreStillValid(restore), () => this.isRestoreCurrent(restore));
		if (reach === 'cancelled' || !this.restoreStillValid(restore)) return;
		if (reach === 'end') {
			const max = el.scrollHeight - el.clientHeight;
			console.warn(`${MenuPage.SCROLL_LOG} alvo ${Math.round(target())}px passa do fim do cardápio (${Math.round(max)}px); pousando truncado`);
			restore.motivo = 'fim_do_cardapio';
		}

		this.settleStickyBarFor(target());
		const wanted = target();
		el.scrollTop = wanted;
		// `scrollTop` é TRUNCADO em silêncio quando o conteúdo não alcança: ler de volta é a
		// única forma de saber que a pessoa não pousou onde estava (52.629 px na 8466, base).
		const desvio = el.scrollTop - wanted;
		restore.seen = this.snapshot(node ?? listRef);
		restore.landed = true;
		this.reportRestore(restore, restore.motivo ? 'degradou' : 'pousou', desvio);
		// Pousou: o que o pump montar daqui em diante entra abaixo e não move a tela.
		restore.releasePump?.();
		restore.releasePump = null;
		if (this.contentAboveSettled) this.endRestore();
	}

	private fallbackLabel(anchor: ScrollAnchor): string {
		return anchor.listOffset != null ? `pelo topo da lista (${Math.round(anchor.listOffset)}px)` : `no pixel ${anchor.absoluteY}`;
	}

	/**
	 * Layout novo: põe a barra sticky no estado que ela vai ter em `scrollTop = target` ANTES
	 * de medir o pouso. A âncora foi capturada com a barra grudada (a pessoa estava funda) e a
	 * página nova nasce solta; o pouso disparava o `onScroll`, a barra grudava e o conteúdo
	 * acima da lista crescia ~21 px (pills + busca abaixo = 120 px; select + espaçador = 141 px
	 * no celular). O Chrome compensa por scroll anchoring; o WebKit não, e a volta ficava 21 px
	 * abaixo. A regra é a do `onScroll`: gruda quando o topo da barra encosta no topo.
	 */
	private settleStickyBarFor(target: number) {
		if (!this.isNewHomePedirDelivery || this.isSticky) return;
		const bar = this.stickyBarRef?.nativeElement;
		const el = this.scrollEl;
		if (!bar || !el) return;
		// o topo da barra na viewport, com a página em `target` (sticky: não sobe além do topo do container)
		const containerTop = el.getBoundingClientRect().top;
		if (containerTop + Math.max(0, this.scrollOffsetOf(bar, el) - target) > 1) return;
		this.isSticky = true;
		this.cdr.detectChanges();
	}

	/**
	 * O conteúdo acima da lista (carrossel de recomendações) chegou. Ele empurra a lista para
	 * baixo, então quem já pousou pousa de novo pela âncora — no mesmo CONTEÚDO, não no mesmo
	 * pixel. Quem ainda não pousou vai medir depois disto. Quem já rolou fica onde está.
	 */
	private onContentAboveSettled() {
		this.contentAboveSettled = true;
		const restore = this.restore;
		if (!restore || !this.restoreStillValid(restore)) return;
		if (!restore.landed) return;
		this.land(restore).catch(error => {
			console.warn(`${MenuPage.SCROLL_LOG} segundo pouso abortado por erro`, error);
			if (this.restore === restore) this.endRestore();
		});
	}

	/**
	 * A lista terminou de montar (`listReady` do pump). É a régua de MECANISMO: o
	 * `ClientMenuRendered` diz quando o primeiro pixel apareceu, este diz quanto custou o
	 * resto — e traz o TAMANHO do cardápio, que o funil nunca teve
	 * (`ClientMenuViewed.out.products_count` sai 0 desde sempre).
	 */
	onListReady(stats: PumpStats) {
		this.orderFlowTrace.emit('ClientMenuListReady', {
			out: {
				produtos: stats.produtos,
				categorias: stats.categorias,
				primeiro_card_ms: Math.round(stats.primeiroCardMs),
				pronto_ms: Math.round(stats.prontoMs),
				tasks: stats.tasks,
				pior_task_ms: Math.round(stats.piorTaskMs),
				blocking_ms: Math.round(stats.blockingMs),
			},
		}, 'MenuPage.onListReady');
	}

	async openSearchModal() {
		const modal = await this.modalController.create({
			component: SearchModalComponent,
			componentProps: { restaurant: this.restaurant },
			cssClass: 'search-modal',
		});
		await modal.present();
	}

	private getFirstCategoryValue(): string {
		if (this.itemsToHide && !this.itemsToHide.featuredItems) return 'featuredItems';
		if (this.itemsToHide && !this.itemsToHide.combos) return 'combos';
		if (this.itemsToHide && !this.itemsToHide.pizzas) return 'pizzas';
		if (this.categories && this.categories.length) return `${this.categories[0].name}-${this.categories[0].id}`;
		return '';
	}

	async promptPush() {
		if(!this.onesignalService.isInitied) await this.onesignalService.initOneSignal();

        let oneSignalSupported = await this.onesignalService.isSupported();
        if (!oneSignalSupported) console.error("OneSignal: Browser not compatible!");

        this.onesignalService.sendTags()

        let oneSignalEnabled = await this.onesignalService.isEnabled();
        if (!oneSignalEnabled) return;

        this.onesignalService.getUserID().then(userID => {
            this.localStorageService.setItem('OneSignalID', userID)
        }, error => {
            console.log('Error:', error.error)
        })
	}

	async onScroll() {
		if (this.isNewHomePedirDelivery) {
			const bar = this.stickyBarRef?.nativeElement;
			if (bar) {
				const top = bar.getBoundingClientRect().top;
				const stickyLeavePx = 5;
				if (top <= 1) {
					if (!this.isSticky) this.isSticky = true;
				} else if (top > stickyLeavePx) {
					if (this.isSticky) this.isSticky = false;
				}
			}

			this.currentCategoryValue = this.categoryInView();
	} else {
		const elements = [
			{ id: 'featuredItems', category: 'featuredItems-category' },
			{ id: 'combos', category: 'combos-category' },
			{ id: 'pizzas', category: 'pizzas-category' },
		];

		for (const item of elements) {
			const element = document.getElementById(item.id);
			if (element) {
				const rect = element.getBoundingClientRect();
				if (rect.top <= 100 && rect.bottom >= 100) {
					this.currentCategoryValue = item.id;
					this.elementScrollCategoryNav(item.category);
				}
			}
		}

		for (const item of this.categories) {
			const element = document.getElementById(`${item.name}-${item.id}`);
			if (element) {
				const rect = element.getBoundingClientRect();
				if (rect.top <= 100 && rect.bottom >= 100) {
					this.currentCategoryValue = `${item.name}-${item.id}`;
					this.elementScrollCategoryNav(`general-${item.id}`);
				}
			}
		}
	}
}

	/** Scrollspy do layout novo: a última categoria cujo topo já passou do topo da tela. */
	private categoryInView(): string {
		const threshold = 100;
		let activeValue: string | null = null;

		const specialElements = [
			{ id: 'featuredItems', value: 'featuredItems' },
			{ id: 'combos', value: 'combos' },
			{ id: 'pizzas', value: 'pizzas' },
		];

		for (const item of specialElements) {
			const element = document.getElementById(item.id);
			if (element && element.getBoundingClientRect().top <= threshold && element.getBoundingClientRect().height > 0) {
				activeValue = item.value;
			}
		}

		for (const item of this.categories) {
			const element = document.getElementById(`${item.name}-${item.id}`);
			if (element && element.getBoundingClientRect().top <= threshold && element.getBoundingClientRect().height > 0) {
				activeValue = `${item.name}-${item.id}`;
			}
		}

		return activeValue ?? this.getFirstCategoryValue();
	}

	/* `highlightCategory()` saiu daqui. Ela mexia à mão nas classes do <ion-segment>:
	   tirava `segment-button-checked` de todo `ion-segment-button` e punha no atual, porque
	   o ngModel do segmento não acompanhava a rolagem. Sem Ionic, `ion-segment-button` não
	   casa com nada e `segment-button-checked` não é estilo em lugar nenhum — a marcação da
	   categoria durante a rolagem tinha sumido do cardápio legado. O <category-nav> já sabe
	   se marcar pelo @Input `activeCategoryValue`; é o mesmo caminho do cardápio novo. */

	elementScrollCategoryNav(categoryId: string) {
		const categoryNavElement = document.getElementById('categoryNav');
		const element = document.getElementById(categoryId);
		if (!categoryNavElement || !element) return;

		const navRect = categoryNavElement.getBoundingClientRect();
		const rect = element.getBoundingClientRect();

		const scrollOptions: ScrollToOptions = {
			// posição RELATIVA ao nav (não ao viewport): no desktop o nav fica deslocado
			// da borda da tela; usar rect.left cru rolava a barra pra direita e escondia
			// PROMOÇÃO/COMBOS/PIZZAS. Ver category-nav-scroll.util + spec.
			left: computeCategoryNavScrollLeft(categoryNavElement.scrollLeft, navRect.left, rect.left),
			behavior: 'smooth',
		};
		categoryNavElement.scrollTo(scrollOptions);
	}

	async loadRecommendedProducts() {
		if (this.recommendProductService.checkCanShowRecommendedProducts()) {
			this.recommendProductService.setAvailableProducts(this.availableProducts);
			await this.recommendProductService.getRecommendedProducts().then((recommendedProducts) => {
				this.recommendedProducts = recommendedProducts;
			});
		}
	}

	/**
	 * Monta os dois links do banner do rodapé (a logo e a chamada de texto).
	 *
	 * Dois campos, e não uma função no `[href]`: binding a função reavalia a
	 * cada change detection, e num cardápio grande a CD roda o tempo todo. Ver
	 * `perf-antipatterns-evitar`.
	 *
	 * A montagem da URL (UTMs) vive em `footer-banner-link.ts`, testada sem
	 * TestBed — é contrato com o marketing, não detalhe de componente.
	 */
	setFooterLink() {
		this.footerVariant = pickFooterBannerVariant();
		const slug = this.bannerSlug();
		this.footerLinkLogo = buildFooterBannerLink(this.footerVariant, slug, 'logo');
		this.footerLinkCta = buildFooterBannerLink(this.footerVariant, slug, 'cta');
		this.footerLinkText = FOOTER_BANNER_TEXT[this.footerVariant];
	}

	/** Slug da loja (`restaurant_url`) — a chave que vai no UTM e na telemetria. */
	private bannerSlug(): string {
		try {
			return (this.restaurant?.info?.restaurant_url || '').toString();
		} catch (e) {
			return '';
		}
	}

	/**
	 * Liga o DENOMINADOR do banner: emite uma vez, quando o rodapé de fato
	 * entra na viewport.
	 *
	 * Uma vez por montagem da página, e o observer se desconecta em seguida —
	 * quem rola para cima e volta não conta duas vezes. Sem
	 * `IntersectionObserver` (browsers antigos: o piso do browserslist é
	 * `ios_saf 13.4`, que tem, mas UA forjado e webview exótico existem) o
	 * denominador simplesmente não sai; ele é opcional por natureza, o clique
	 * é que não pode faltar.
	 *
	 * Fora da zone do Angular: o callback não deve disparar CD global no meio
	 * de um scroll de cardápio grande.
	 */
	private watchFooterBanner() {
		if (typeof IntersectionObserver === 'undefined') return;
		const alvo = this.footerBannerRef?.nativeElement;
		if (!alvo) return;

		this.ngZone.runOutsideAngular(() => {
			this.bannerObserver = new IntersectionObserver((entradas) => {
				if (!entradas.some(e => e.isIntersecting)) return;
				this.disconnectBannerObserver();
				if (this.bannerViewedAt === 0) this.bannerViewedAt = performance.now();
				this.emitBannerViewed();
			}, { threshold: 0.5 });
			this.bannerObserver.observe(alvo);
		});
	}

	private disconnectBannerObserver() {
		try {
			this.bannerObserver?.disconnect();
		} catch (e) {
			// observer já morto — irrelevante
		}
		this.bannerObserver = null;
	}

	/**
	 * Impressão do banner — o DENOMINADOR.
	 *
	 * Amostrada a 10% dos devices dentro do serviço (`emitViewed` decide), não
	 * aqui: a fração e o carimbo que o clique carrega têm de sair da MESMA
	 * fonte, senão divergem no dia em que alguém mexer num dos dois.
	 */
	private emitBannerViewed() {
		if (this.bannerViewEmitted) return;
		this.bannerViewEmitted = true;
		this.mpBannerTrace.emitViewed({
			slug: this.bannerSlug(),
			variant: this.footerVariant,
			...this.bannerContext(),
		}, 'menu.watchFooterBanner');
	}

	/**
	 * Clique no banner — o NUMERADOR, e a resposta às perguntas do marketing.
	 *
	 * Não cancela nem atrasa a navegação: o `<a href>` segue seu curso e o
	 * evento sai por `sendBeacon`, que o browser entrega mesmo se o documento
	 * for descarregado. `preventDefault` aqui trocaria um link que sempre
	 * funciona por uma navegação por JS que depende de a telemetria não falhar.
	 */
	onFooterBannerClick(slot: FooterBannerSlot) {
		const slug = this.bannerSlug();
		const href = slot === 'logo' ? this.footerLinkLogo : this.footerLinkCta;

		this.mpBannerTrace.emitClicked({
			slug,
			variant: this.footerVariant,
			slot,
			...this.bannerContext(),
			cart_total: this.cartService.total(),
			ms_since_view: this.bannerViewedAt
				? Math.round(performance.now() - this.bannerViewedAt)
				: -1,
			// Este device pertence à população que gera as impressões? É o que
			// torna a taxa calculável apesar de a impressão ser amostrada e o
			// clique não — sem fator de correção fora do dado.
			in_view_sample: this.mpBannerTrace.inViewSample(),
			target_url: href,
		}, 'menu.onFooterBannerClick');

		// GA4: o funil cobre a coorte + a frota nos FLEET_STEPS, mas quem lê
		// aquisição é o marketing, no painel deles. `select_promotion` é o
		// evento recomendado do GA4 para banner interno.
		try {
			gtag('event', 'select_promotion', {
				promotion_id: 'rodape-pedir-delivery',
				promotion_name: 'Rodape Pedir.Delivery',
				creative_name: this.footerVariant,
				creative_slot: slot,
				restaurant_slug: slug,
				restaurant_id: this.restaurant?.info?.id,
			});
		} catch (e) {
			// gtag pode não existir (bloqueador, loja sem GA): telemetria própria já saiu
		}
	}

	/**
	 * Contexto comum à impressão e ao clique — "em que etapa da página" e
	 * "qual dispositivo".
	 *
	 * Defensivo em tudo: roda no caminho de um clique real e não pode lançar.
	 */
	private bannerContext(): any {
		// `-1` = não deu para medir; `100` = a página não rola (cardápio curto —
		// o rodapé já nasce visível). Os dois são respostas, não ausência de
		// dado, e por isso não podem virar o mesmo número que "rolou até o fim".
		let scrollPct = -1;
		try {
			const el = this.scrollEl;
			const h = el ? el.scrollHeight - el.clientHeight : -1;
			if (h > 0) scrollPct = Math.round((this.currentScrollTop / h) * 100);
			else if (h === 0) scrollPct = 100;
		} catch (e) {
			scrollPct = -1;
		}
		const info: any = this.restaurant?.info || {};
		return {
			scroll_pct: scrollPct,
			ms_on_page: this.pageEnteredAt ? Math.round(performance.now() - this.pageEnteredAt) : -1,
			scrolled: this.currentScrollTop > 0,
			cart_items: (this.cartService.products || []).length,
			is_closed: !!info.is_closed,
			cashier_is_open: !!info.cashier_is_open,
			viewport_w: window.innerWidth,
			viewport_h: window.innerHeight,
			dpr: window.devicePixelRatio,
			// PWA instalado na tela inicial: público diferente de quem chega por link.
			standalone: !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches),
			lang: (navigator.language || '').slice(0, 10),
		};
	}

	getCurrentYear(): number {
		return new Date().getFullYear();
	}

	isInstagram(): boolean {
		const ua = window.navigator.userAgent || '';
		return ua.indexOf('Instagram') > -1;
	}

	isIOS(): boolean {
		const ua = window.navigator.userAgent || '';
		return /iPhone|iPad|iPod/i.test(ua);
	}

	closeInstagramIosBanner() {
		this.showInstagramIosBanner = false;
	}

}
